import { saveAs } from 'file-saver';
import { Zip, ZipDeflate } from 'fflate';

function abortError() {
  const err = new Error('下载已取消');
  err.name = 'AbortError';
  return err;
}

/**
 * fetch 带空闲超时和外部取消：60s 无数据块视为卡死，收到数据即重置定时器。
 * 外部 signal 会直接中止 fetch 及尚未结束的响应流。
 */
export async function fetchWithIdleTimeout(url, name, signal) {
  if (signal && signal.aborted) throw abortError();

  const controller = new AbortController();
  let timer;
  let timedOut = false;
  const onAbort = () => controller.abort();
  if (signal) signal.addEventListener('abort', onAbort, { once: true });
  const cleanup = () => {
    clearTimeout(timer);
    if (signal) signal.removeEventListener('abort', onAbort);
  };
  const arm = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 60000);
  };

  let resp;
  try {
    arm();
    resp = await fetch(url, { signal: controller.signal });
  } catch (err) {
    cleanup();
    if (signal && signal.aborted) throw abortError();
    if (timedOut) throw new Error(`下载超时: ${name}`);
    throw err;
  }

  if (!resp.body) {
    cleanup();
    return resp;
  }

  const reader = resp.body.getReader();
  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    cleanup();
  };
  const stream = new ReadableStream({
    async pull(pullController) {
      try {
        const { done, value } = await reader.read();
        if (done) {
          finish();
          pullController.close();
          return;
        }
        arm(); // 有数据到达，重置空闲计时
        pullController.enqueue(value);
      } catch (err) {
        finish();
        if (timedOut) pullController.error(new Error(`下载超时: ${name}`));
        else pullController.error(err);
      }
    },
    async cancel(reason) {
      finish();
      try {
        await reader.cancel(reason);
      } catch (err) {
        // fetch 被取消时底层 reader 可能已自行关闭
      }
    },
  });
  arm();
  return new Response(stream, {
    status: resp.status,
    statusText: resp.statusText,
    headers: resp.headers,
  });
}

/** 从预签名 URL 拉取 blob 并用 file-saver 保存 */
export async function downloadFromUrl(url, fileName) {
  const blobRes = await fetch(url);
  if (!blobRes.ok) throw new Error(`下载失败: ${blobRes.statusText}`);
  const blob = await blobRes.blob();
  saveAs(blob, fileName);
}

/**
 * 流式拼 zip 写盘：同步弹保存框 → 拉清单（后端签发预签名 URL）→ fflate 流式压缩写盘。
 * 内存恒定，支持中途取消；UI 副作用经回调交给调用方，本函数不碰组件状态。
 *
 * @param {Function} getFiles 返回 [{name, url}] 的清单（保存框之后才拉，保留用户手势）
 * @param {String} suggestedName 保存框默认文件名
 * @param {Object} options
 *  - onStart(total) 开始打包（弹进度框）
 *  - onFiles(files) 清单已取得，交给界面一次性建立全部文件行
 *  - onFileStatus(index, status, error) 单文件状态：downloading / done / failed / cancelled
 *  - onProgress(percent, text) 进度推进
 *  - onClose() 收尾（关进度框）
 *  - isCancelled() 用户是否点了取消
 *  - signal 外部取消信号；会中止清单请求、活动下载和写盘
 * @returns {Promise<{status, count?, message?, stage?}>}
 *  status: done | cancelled | empty | error（stage=save 表示连保存框都没过，调用方自行给提示）
 */
export async function streamZipToDisk(getFiles, suggestedName, options = {}) {
  const {
    onStart, onFiles, onFileStatus, onProgress, onClose,
    isCancelled = () => false, signal,
  } = options;
  const close = () => { if (onClose) onClose(); };
  const cancelled = () => isCancelled() || Boolean(signal && signal.aborted);
  const markFile = (index, status, error) => {
    if (onFileStatus && index >= 0) onFileStatus(index, status, error);
  };

  let writable;
  try {
    // showSaveFilePicker 需在用户手势内同步调用（函数首个 await 前），故清单在保存框之后拉取
    writable = await window.showSaveFilePicker({
      suggestedName,
      types: [{ description: 'ZIP 压缩包', accept: { 'application/zip': ['.zip'] } }],
    }).then(handle => handle.createWritable());
  } catch (err) {
    if (err && err.name === 'AbortError') return { status: 'cancelled', stage: 'save' };
    return { status: 'error', stage: 'save', message: '无法创建文件，请重试' };
  }

  if (cancelled()) {
    try { await writable.abort(); } catch (e) { /* 忽略 */ }
    return { status: 'cancelled', stage: 'download' };
  }

  // 拉取授权文件清单（后端仅鉴权并签发 URL）
  let files;
  try {
    files = await getFiles(signal);
  } catch (err) {
    try { await writable.abort(); } catch (e) { /* 忽略 */ }
    if (cancelled()) return { status: 'cancelled', stage: 'download' };
    return { status: 'error', stage: 'manifest', message: err.message };
  }
  if (!files || files.length === 0) {
    try { await writable.abort(); } catch (e) { /* 忽略 */ }
    return { status: 'empty' };
  }

  if (onStart) onStart(files.length);
  if (onFiles) onFiles(files);

  // 写盘队列：ondata 同步回调经 promise 链写入磁盘
  let writeChain = Promise.resolve();
  const zip = new Zip();
  zip.ondata = (err, chunk) => {
    if (err) return;
    writeChain = writeChain.then(() => writable.write(chunk));
  };

  let activeIndex = -1;
  try {
    for (let i = 0; i < files.length; i++) {
      if (cancelled()) break;
      const f = files[i];
      activeIndex = i;
      markFile(i, 'downloading');
      if (onProgress) {
        const pct = Math.min(99, Math.round((i / files.length) * 100));
        onProgress(pct, `正在下载 ${i + 1}/${files.length}... 已完成 ${i}`);
      }
      const zf = new ZipDeflate(f.name, { level: 1 });
      zip.add(zf);
      const resp = await fetchWithIdleTimeout(f.url, f.name, signal);
      if (!resp.ok || !resp.body) {
        if (resp.body) {
          try { await resp.body.cancel(); } catch (e) { /* 释放错误响应流 */ }
        }
        throw new Error(`下载失败: ${f.name} (HTTP ${resp.status})`);
      }
      if (cancelled()) {
        try { await resp.body.cancel(); } catch (e) { /* signal 已中止流 */ }
        break;
      }
      const reader = resp.body.getReader();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        if (cancelled()) {
          try { await reader.cancel(); } catch (e) { /* signal 已中止流 */ }
          markFile(i, 'cancelled');
          break;
        }
        zf.push(value);
      }
      if (cancelled()) break;
      zf.push(new Uint8Array(0), true);
      // 防御性内存清理：释放压缩器内部缓冲
      zip.u[zip.u.length - 1].d = null;
      markFile(i, 'done');
      activeIndex = -1;
      if (onProgress) {
        const pct = Math.min(99, Math.round(((i + 1) / files.length) * 100));
        onProgress(pct, `已处理 ${i + 1}/${files.length} 个文件... 整体 ${pct}%`);
      }
    }
    // 取消：abort 丢弃写入并删除已创建的文件
    if (cancelled()) {
      try { await writable.abort(); } catch (e) { /* 忽略 */ }
      close();
      return { status: 'cancelled', stage: 'download' };
    }
    zip.end();
    await writeChain;
    await writable.close();
    close();
    return { status: 'done', count: files.length };
  } catch (err) {
    markFile(activeIndex, cancelled() ? 'cancelled' : 'failed', err.message || '下载失败');
    close();
    try { await writable.abort(); } catch (e) { /* 忽略 */ }
    if (cancelled()) return { status: 'cancelled', stage: 'download' };
    return { status: 'error', stage: 'download', message: err.message || '未知错误' };
  }
}
